class CurrentVacancies extends HTMLElement {
  constructor() {
    super();

    this.groups = ["department", "job_type", "location"];
    this.selected = {
      department: new Set(),
      job_type: new Set(),
      location: new Set(),
    };
  }

  connectedCallback() {
    this.cards = Array.from(
      this.querySelectorAll("[data-current-vacancies-card]"),
    );
    this.filters = Array.from(
      this.querySelectorAll("[data-current-vacancies-filter]"),
    );
    this.countEls = Array.from(
      this.querySelectorAll("[data-current-vacancies-count]"),
    );
    this.resetButtons = Array.from(
      this.querySelectorAll("[data-current-vacancies-reset]"),
    );
    this.chipsEl = this.querySelector("[data-current-vacancies-chips]");
    this.emptyEl = this.querySelector("[data-current-vacancies-empty]");
    this.listEmptyEl = this.querySelector(
      "[data-current-vacancies-list-empty]",
    );
    this.openButton = this.querySelector("[data-current-vacancies-open]");
    this.closeButtons = Array.from(
      this.querySelectorAll("[data-current-vacancies-close]"),
    );
    this.applyButton = this.querySelector("[data-current-vacancies-apply]");
    this.toggleButtons = Array.from(
      this.querySelectorAll("[data-current-vacancies-toggle]"),
    );
    this.teacherCta = this.querySelector(
      "[data-current-vacancies-teacher-cta]",
    );
    this.dependentGroups = Array.from(
      this.querySelectorAll("[data-current-vacancies-dependent]"),
    );
    this.teacherValue = this.dataset.teacherValue || "teachers";
    this.otherValue = this.dataset.otherValue || "others";

    this.bindEvents();
    this.updateSelectedFromInputs();
    this.ensureDepartmentMode();
    this.preparePanels();
    this.render();
  }

  bindEvents() {
    this.filters.forEach((filter) => {
      filter.addEventListener("change", () => {
        if (filter.dataset.filterType === "department") {
          this.handleDepartmentChange(filter);
          return;
        }

        this.updateSelectedFromInputs();
        this.render();
      });
    });

    this.resetButtons.forEach((button) => {
      button.addEventListener("click", () => this.resetFilters());
    });

    this.toggleButtons.forEach((button) => {
      button.addEventListener("click", () => this.toggleGroup(button));
    });

    if (this.openButton) {
      this.openButton.addEventListener("click", () => this.openDrawer());
    }

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", () => this.closeDrawer());
    });

    if (this.applyButton) {
      this.applyButton.addEventListener("click", () => this.closeDrawer());
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.classList.contains("is-drawer-open")) {
        this.closeDrawer();
      }
    });
  }

  preparePanels() {
    this.toggleButtons.forEach((button, index) => {
      const group = button.closest("[data-current-vacancies-group]");
      const panel = this.getPanel(button);
      const isOpen = index === 0;

      if (!group || !panel) return;

      button.setAttribute("aria-expanded", String(isOpen));
      group.classList.toggle("is-open", isOpen);
      panel.style.maxHeight = isOpen ? `${panel.scrollHeight}px` : "0px";
    });
  }

  updateSelectedFromInputs() {
    this.groups.forEach((group) => this.selected[group].clear());

    this.filters.forEach((filter) => {
      if (!filter.checked) return;

      const group = filter.dataset.filterType;

      if (this.selected[group]) {
        this.selected[group].add(filter.value);
      }
    });
  }

  handleDepartmentChange(activeFilter) {
    const departmentFilters = this.getFiltersByType("department");

    if (activeFilter.checked) {
      departmentFilters.forEach((filter) => {
        filter.checked = filter === activeFilter;
      });
    }

    this.updateSelectedFromInputs();
    this.ensureDepartmentMode();
    this.render();
  }

  ensureDepartmentMode() {
    const departmentFilters = this.getFiltersByType("department");
    const hasDepartment = departmentFilters.some((filter) => filter.checked);
    const teacherFilter = departmentFilters.find(
      (filter) => filter.value === this.teacherValue,
    );

    if (!hasDepartment && teacherFilter) {
      teacherFilter.checked = true;
      this.updateSelectedFromInputs();
    }

    if (this.isTeacherMode()) {
      this.selected.job_type.clear();
      this.selected.location.clear();
      this.syncInputs();
    }
  }

  getFiltersByType(type) {
    return this.filters.filter((filter) => filter.dataset.filterType === type);
  }

  syncInputs() {
    this.filters.forEach((filter) => {
      const group = filter.dataset.filterType;
      filter.checked =
        this.selected[group] && this.selected[group].has(filter.value);
    });
  }

  getFilteredCards() {
    if (this.isTeacherMode()) {
      return [];
    }

    return this.cards.filter((card) => {
      return ["job_type", "location"].every((group) => {
        const value = this.getCardValue(card, group);
        return (
          this.selected[group].size === 0 || this.selected[group].has(value)
        );
      });
    });
  }

  getCardValue(card, group) {
    if (group === "job_type") {
      return card.dataset.jobType;
    }

    return card.dataset[group];
  }

  hasActiveFilters() {
    // Department is only "active" if something other than the default teacher is selected
    const departmentActive =
      !this.selected.department.has(this.teacherValue) ||
      this.selected.department.size > 1;

    const otherFiltersActive = ["job_type", "location"].some(
      (group) => this.selected[group].size > 0,
    );

    return departmentActive || otherFiltersActive;
  }

  isTeacherMode() {
    return this.selected.department.has(this.teacherValue);
  }

  render() {
    const filteredCards = this.getFilteredCards();
    const visibleCards = new Set(filteredCards);
    const hasActiveFilters = this.hasActiveFilters();

    this.cards.forEach((card) => {
      card.hidden = !visibleCards.has(card);
    });

    if (this.emptyEl) {
      this.emptyEl.hidden =
        this.isTeacherMode() ||
        filteredCards.length > 0 ||
        this.cards.length === 0;
    }

    if (this.listEmptyEl) {
      this.listEmptyEl.hidden = this.isTeacherMode() && this.cards.length === 0;
    }

    this.classList.toggle("is-teacher-mode", this.isTeacherMode());
    this.renderTeacherCta();
    this.renderDependentFilters();
    this.renderCount(filteredCards.length);
    this.renderChips();
    this.renderResetButtons(hasActiveFilters);
  }

  renderTeacherCta() {
    if (!this.teacherCta) return;

    this.teacherCta.hidden = !this.isTeacherMode();
  }

  renderDependentFilters() {
    const disabled = this.isTeacherMode();

    this.dependentGroups.forEach((group) => {
      const toggle = group.querySelector("[data-current-vacancies-toggle]");
      const filters = Array.from(
        group.querySelectorAll("[data-current-vacancies-filter]"),
      );

      group.classList.toggle("is-disabled", disabled);

      if (toggle) {
        toggle.disabled = disabled;

        if (disabled) {
          this.closeGroup(toggle);
        }
      }

      filters.forEach((filter) => {
        filter.disabled = disabled;
      });
    });
  }

  renderCount(count) {
    const label = this.dataset.totalLabel || "Total Results";

    this.countEls.forEach((countEl) => {
      countEl.textContent = `${label} - ${count}`;
    });
  }

  renderChips() {
    if (!this.chipsEl) return;

    this.chipsEl.innerHTML = "";

    if (this.isTeacherMode()) return;

    this.groups.forEach((group) => {
      Array.from(this.selected[group]).forEach((value) => {
        const filter = this.filters.find(
          (item) => item.dataset.filterType === group && item.value === value,
        );
        const label = filter ? filter.dataset.filterLabel : value;
        const chip = document.createElement("button");

        chip.className = "current_vacancies__chip";
        chip.type = "button";
        chip.dataset.filterType = group;
        chip.dataset.filterValue = value;
        chip.setAttribute("aria-label", `Remove ${label} filter`);
        chip.innerHTML = `<span class="current_vacancies__chip-label">${label}</span><span aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 7L7 17M7 7L17 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>`;

        chip.addEventListener("click", () => {
          this.selected[group].delete(value);
          this.syncInputs();
          this.ensureDepartmentMode();
          this.render();
        });

        this.chipsEl.appendChild(chip);
      });
    });
  }

  renderResetButtons(hasActiveFilters) {
    this.resetButtons.forEach((button) => {
      button.disabled = !hasActiveFilters;
    });
  }

  resetFilters() {
    this.groups.forEach((group) => this.selected[group].clear());
    const teacherFilter = this.filters.find(
      (filter) =>
        filter.dataset.filterType === "department" &&
        filter.value === this.teacherValue,
    );

    if (teacherFilter) {
      teacherFilter.checked = true;
      this.selected.department.add(this.teacherValue);
    }

    this.syncInputs();
    this.ensureDepartmentMode();
    this.render();
  }

  toggleGroup(button) {
    const group = button.closest("[data-current-vacancies-group]");
    const panel = this.getPanel(button);
    const isOpen = button.getAttribute("aria-expanded") === "true";

    if (!group || !panel) return;

    if (!isOpen) {
      this.closeOtherGroups(group);
    }

    button.setAttribute("aria-expanded", String(!isOpen));
    group.classList.toggle("is-open", !isOpen);

    if (isOpen) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
      requestAnimationFrame(() => {
        panel.style.maxHeight = "0px";
      });
    } else {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  closeOtherGroups(activeGroup) {
    this.toggleButtons.forEach((button) => {
      const group = button.closest("[data-current-vacancies-group]");
      if (!group || group === activeGroup) return;

      this.closeGroup(button);
    });
  }

  closeGroup(button) {
    const group = button.closest("[data-current-vacancies-group]");
    const panel = this.getPanel(button);

    if (!group || !panel) return;

    button.setAttribute("aria-expanded", "false");
    group.classList.remove("is-open");
    panel.style.maxHeight = "0px";
  }

  getPanel(button) {
    const panelId = button.getAttribute("aria-controls");
    return panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
  }

  openDrawer() {
    this.classList.add("is-drawer-open");
    document.documentElement.classList.add("current_vacancies-drawer-lock");
  }

  closeDrawer() {
    this.classList.remove("is-drawer-open");
    document.documentElement.classList.remove("current_vacancies-drawer-lock");
  }
}

if (!customElements.get("current-vacancies")) {
  customElements.define("current-vacancies", CurrentVacancies);
}